/**
 * Type Definitions for ORCA360 CBTA Trainee Sim Assessment Export
 *
 * These types define the structure for exporting the pilot assessment
 * to PDF and Word formats, following the ORCA360 CBTA template.
 * 
 * Template Structure:
 * - Part A: Session Identification
 * - Part B: Threat & Error Management (TEM) Overview
 * - Part C: Competency Assessment (9 competencies)
 * - Part D: Root Cause Analysis & Training Recommendations
 */

import type { DebriefingPartAData } from './debriefing-export';

// =====================================================================
// COMPETENCY DEFINITIONS
// =====================================================================

export const PILOT_COMPETENCIES = [
  'PRO', 'COM', 'FPA', 'FPM', 'KNO', 'LTW', 'PSD', 'SAW', 'WLM'
] as const;

export type PilotCompetency = typeof PILOT_COMPETENCIES[number];

export const COMPETENCY_FULL_NAMES: Record<PilotCompetency, string> = {
  PRO: 'Application of Procedures',
  COM: 'Communication',
  FPA: 'Flight Path Management - Automation',
  FPM: 'Flight Path Management - Manual',
  KNO: 'Application of Knowledge',
  LTW: 'Leadership & Teamwork',
  PSD: 'Problem Solving & Decision Making',
  SAW: 'Situational Awareness',
  WLM: 'Workload Management',
};

export const COMPETENCY_ORDER: PilotCompetency[] = [
  'PRO', 'COM', 'FPA', 'FPM', 'KNO', 'LTW', 'PSD', 'SAW', 'WLM'
];

// =====================================================================
// GRADE DEFINITIONS
// =====================================================================

export const GRADE_LABELS: Record<number, string> = {
  5: 'Excellent',
  4: 'Above Standard',
  3: 'Standard',
  2: 'Below Standard',
  1: 'Unsatisfactory',
};

// =====================================================================
// Part A: Session Identification (reuse from debriefing-export)
// =====================================================================

// Re-export DebriefingPartAData as PilotAssessmentPartA for clarity
export type PilotAssessmentPartA = DebriefingPartAData;

// =====================================================================
// Part B: TEM Overview
// =====================================================================

/** Threat category types matching ORCA360 TEM framework */
export type ThreatCategory =
  | 'Environmental'
  | 'Organizational'
  | 'Aircraft'
  | 'Airport'
  | 'ATC'
  | 'Weather'
  | 'Terrain'
  | 'Other'
  | 'Environmental/Operational'; // Legacy fallback

/** Threat management status */
export type ThreatManagement = 'Managed' | 'Mismanaged' | 'Unmanaged';

export interface TEMThreat {
  /** Threat category (Environmental, Organizational, Aircraft, etc.) */
  category: string;
  /** Description of the threat */
  description?: string;
  /** Primary competency affected by this threat */
  primaryCompetency: PilotCompetency;
  /** Secondary competencies affected */
  secondaryCompetencies: PilotCompetency[];
  /** How the threat was managed */
  management?: ThreatManagement;
  /** Evidence from transcript */
  evidence?: string;
}

/** Error type classification */
export type ErrorType = 'Procedural' | 'Communication' | 'Proficiency' | 'Operational Decision' | 'Other';

/** Error classification */
export type ErrorClassification = 'Commission' | 'Omission';

export interface TEMError {
  /** Type of error */
  type: ErrorType;
  /** Classification (commission or omission) */
  classification?: ErrorClassification;
  /** Description of the error */
  description: string;
  /** Whether the error led to UAS */
  ledToUAS?: boolean;
  /** Evidence from transcript */
  evidence?: string;
}

export interface TEMResilienceFactor {
  /** Description of the resilience factor */
  description: string;
  /** Competencies that contributed to resilience */
  competencies?: PilotCompetency[];
  /** Evidence from transcript */
  evidence?: string;
}

export interface TEMUAS {
  /** Description of the Undesired Aircraft State */
  description: string;
  /** Severity: Minor, Moderate, Serious */
  severity?: 'Minor' | 'Moderate' | 'Serious';
  /** How the crew recovered */
  recovery?: string;
  /** Evidence from transcript */
  evidence?: string;
}

export interface PilotAssessmentPartB {
  /** Brief description of operational context and TEM management */
  temOverview: string;
  /** Threats identified during session */
  threats: TEMThreat[];
  /** Errors identified during session (structured or string fallback) */
  errors: (TEMError | string)[];
  /** Resilience factors demonstrated (structured or string fallback) */
  resilienceFactors: (TEMResilienceFactor | string)[];
  /** Undesired Aircraft State events */
  uasEvents: TEMUAS[];
}

// =====================================================================
// ORCA360 V2 Part B Database Format (from orca360_assessments.part_b)
// =====================================================================

/** Database format for Part B from orca360_assessments table */
export interface ORCA360PartBDatabase {
  operationalContext?: string;
  threats?: Array<{
    category: string;
    description: string;
    primaryCompetency: string;
    secondaryCompetencies?: string[];
    management?: string;
    evidence?: string;
  }>;
  errors?: Array<{
    type?: string;
    classification?: string;
    description: string;
    ledToUAS?: boolean;
    evidence?: string;
  }>;
  resilienceFactors?: Array<{
    description: string;
    competencies?: string[];
    evidence?: string;
  }>;
  uasEvents?: Array<{
    description: string;
    severity?: string;
    recovery?: string;
    evidence?: string;
  }>;
  managementSummary?: string;
}

// =====================================================================
// Part C: Competency Assessment
// =====================================================================

export interface ObservableBehavior {
  /** OB code from WingMentor Core Framework, e.g., "COM-3", "KNO-7" */
  code: string;
  /** Short description of the behavior */
  description: string;
}

export interface EvidenceItem {
  /** Direct quote from transcript */
  quote: string;
  /** Timestamp of the evidence, e.g., "14:23:05" */
  timestamp: string;
  /** Validation status from anti-hallucination system */
  validation_status: 'VERIFIED' | 'PARAPHRASED' | 'SIMILAR' | 'UNVERIFIED' | 'VERBATIM';
  /** Confidence score (0-1) */
  confidence?: number;
  /** LLM validation decision (Tier 4) */
  llm_decision?: 'ACCEPTED' | 'REJECTED' | 'RECLASSIFIED';
  /** LLM reasoning for decision */
  llm_reasoning?: string;
  /** Observable Behavior code, e.g., "COM-3", "KNO-7" - LLM-extracted */
  obCode?: string;
  /** Source of the OB code: 'llm' (validated against framework) or 'fallback' (sentinel code) */
  obCodeSource?: 'llm' | 'fallback';
}

export interface CompetencyAssessment {
  /** Competency code (PRO, COM, etc.) */
  competency: PilotCompetency;
  /** Grade 1-5 */
  score: number;
  /** Observable behaviors demonstrated */
  observableBehaviors: ObservableBehavior[];
  /** Evidence items with quotes and timestamps */
  evidence: EvidenceItem[];
  /** Instructor comments linked to OBs */
  instructorComments: string;
}

export interface PilotAssessmentPartC {
  /** All 9 competency assessments */
  competencyAssessments: CompetencyAssessment[];
}

// =====================================================================
// Part D: Root Cause Analysis & Training Recommendations
// =====================================================================

export interface FocusCompetencyAnalysis {
  /** The competency requiring focus */
  competency: PilotCompetency;
  /** Competency full name */
  competencyName: string;
  /** Why did it happen? (Causal Factor) */
  causalFactor: string;
  /** Remedial Training / Recommendations */
  recommendations: string;
}

export interface PilotAssessmentPartD {
  /** Focus competencies requiring RCA (typically 2-4) */
  focusCompetencies: FocusCompetencyAnalysis[];
}

// =====================================================================
// COMPLETE PILOT ASSESSMENT EXPORT DATA
// =====================================================================

export interface PilotAssessmentExportData {
  partA: PilotAssessmentPartA;
  partB: PilotAssessmentPartB;
  partC: PilotAssessmentPartC;
  partD: PilotAssessmentPartD;
  /** Invocation line at end of report */
  invocationLine?: string;
  /** Metadata */
  metadata?: {
    generatedAt: string;
    version: string;
    traineeId: string;
    pilotRole?: 'captain' | 'captain_2' | 'first_officer' | 'first_officer_2';
  };
}

// =====================================================================
// DATABASE TO EXPORT MAPPING
// =====================================================================

export interface AssessmentPilotRow {
  id: string;
  session_id: string;
  trainee_id: string | null;
  org_pilot_id?: string | null;
  pilot_role?: 'captain' | 'captain_2' | 'first_officer' | 'first_officer_2';
  competency: string;
  score: number;
  evidence: EvidenceItem[];
  analysis: string; // 5-Whys / RCA
  goals: string; // SMART goals / recommendations
  facilitator_questions?: string[];
  manual_observations?: string;
  status: 'draft' | 'published';
  version: number;
  ai_generated: boolean;
  created_at: string;
  updated_at: string;
  approved_at?: string;
  approved_by?: string;
}

export interface SessionForExport {
  id: string;
  session_date: string;
  aircraft_type: string;
  simulator_id?: string;
  scenario_code?: string;
  flight_phase?: string;
  session_notes?: string;
  session_start_time?: string;
  session_end_time?: string;
  time_pf_minutes?: number;
  time_pm_minutes?: number;
  location?: string;
  overall_result?: 'Pass' | 'Fail' | 'Incomplete';
  pilot_function?: 'Captain' | 'First Officer' | 'Instructor Trainee' | 'Other';
  trainee?: {
    id: string;
    full_name: string;
    licence_id?: string;
  };
  instructor?: {
    id: string;
    full_name: string;
    licence_id?: string;
  };
  organization?: {
    id: string;
    name: string;
  };
}

// =====================================================================
// EXPORT CONFIGURATION
// =====================================================================

export interface PilotAssessmentExportOptions {
  format: 'pdf' | 'docx';
  includeLogo?: boolean;
  includeHeaders?: boolean;
  includeFooters?: boolean;
  includePageNumbers?: boolean;
  includePartA?: boolean;
  includePartB?: boolean;
  includePartC?: boolean;
  includePartD?: boolean;
  includeSignatures?: boolean;
}

// =====================================================================
// HELPER FUNCTIONS
// =====================================================================

/**
 * Map database assessment rows to Part C structure
 */
export function mapAssessmentsToPartC(assessments: AssessmentPilotRow[]): PilotAssessmentPartC {
  const competencyAssessments: CompetencyAssessment[] = COMPETENCY_ORDER.map(comp => {
    const assessment = assessments.find(a => a.competency === comp);
    
    if (!assessment) {
      return {
        competency: comp,
        score: 0,
        observableBehaviors: [],
        evidence: [],
        instructorComments: 'Not assessed',
      };
    }

    const evidence = assessment.evidence || [];
    const observableBehaviors: ObservableBehavior[] = Array.from(
      new Set(evidence.map((e) => e.obCode).filter((c): c is string => !!c)),
    ).map((code) => ({ code, description: '' }));

    return {
      competency: comp,
      score: assessment.score || 0,
      observableBehaviors,
      evidence,
      instructorComments: assessment.manual_observations || assessment.analysis || '',
    };
  });

  return { competencyAssessments };
}

/**
 * Map database assessment rows to Part D structure (focus competencies)
 */
export function mapAssessmentsToPartD(assessments: AssessmentPilotRow[]): PilotAssessmentPartD {
  // Focus on competencies with score <= 3 or those with analysis/goals
  const focusCompetencies: FocusCompetencyAnalysis[] = assessments
    .filter(a => a.score != null && a.score <= 3)
    .sort((a, b) => (a.score || 5) - (b.score || 5)) // Lowest scores first
    .map(a => ({
      competency: a.competency as PilotCompetency,
      competencyName: COMPETENCY_FULL_NAMES[a.competency as PilotCompetency] || a.competency,
      causalFactor: a.analysis || 'Analysis not available',
      recommendations: a.goals || 'No specific recommendations',
    }));

  return { focusCompetencies };
}

/**
 * Extract TEM information from assessments for Part B
 */
export function extractTEMFromAssessments(assessments: AssessmentPilotRow[]): PilotAssessmentPartB {
  const threats: TEMThreat[] = [];
  const errors: string[] = [];
  const resilienceFactors: string[] = [];

  // Analyze evidence and analysis text for TEM indicators
  for (const assessment of assessments) {
    const text = `${assessment.analysis || ''} ${assessment.manual_observations || ''}`.toLowerCase();
    
    // Check for threat indicators
    if (text.includes('threat') || text.includes('challenge') || text.includes('complexity')) {
      threats.push({
        category: 'Environmental/Operational',
        primaryCompetency: assessment.competency as PilotCompetency,
        secondaryCompetencies: [],
      });
    }

    // Check for error indicators
    if (text.includes('error') || text.includes('deviation') || text.includes('missed')) {
      const errorMatch = text.match(/(?:error|deviation|missed)[^.]*\./i);
      if (errorMatch) {
        errors.push(errorMatch[0]);
      }
    }

    // Check for resilience indicators
    if (text.includes('recovery') || text.includes('resilience') || text.includes('corrected') || assessment.score >= 4) {
      resilienceFactors.push(`${COMPETENCY_FULL_NAMES[assessment.competency as PilotCompetency]}: Demonstrated proficiency`);
    }
  }

  return {
    temOverview: 'TEM analysis based on session transcript and competency assessment.',
    threats: threats.slice(0, 3),
    errors: errors.slice(0, 5),
    resilienceFactors: resilienceFactors.slice(0, 5),
    uasEvents: [],
  };
}

/**
 * Create complete export data from database records
 */
export function createPilotAssessmentExportData(
  session: SessionForExport,
  assessments: AssessmentPilotRow[],
  partA: PilotAssessmentPartA
): PilotAssessmentExportData {
  return {
    partA,
    partB: extractTEMFromAssessments(assessments),
    partC: mapAssessmentsToPartC(assessments),
    partD: mapAssessmentsToPartD(assessments),
    invocationLine: 'Please verify the assessment and inform the company.',
    metadata: {
      generatedAt: new Date().toISOString(),
      version: '1.0',
      traineeId: session.trainee?.id || '',
    },
  };
}

/**
 * Map ORCA360 V2 Part B data from database to export format
 *
 * This function converts the AI-generated TEM analysis from the
 * orca360_assessments.part_b JSONB column to the export format.
 *
 * @param partBData - The part_b JSONB data from orca360_assessments table
 * @returns PilotAssessmentPartB formatted for PDF/Word export
 */
export function mapORCA360PartBToExport(partBData: ORCA360PartBDatabase | null): PilotAssessmentPartB {
  if (!partBData) {
    return {
      temOverview: 'TEM analysis not available.',
      threats: [],
      errors: [],
      resilienceFactors: [],
      uasEvents: [],
    };
  }

  // Map threats with competency validation
  const threats: TEMThreat[] = (partBData.threats || []).map(threat => {
    // Validate and map primary competency
    const primaryComp = PILOT_COMPETENCIES.includes(threat.primaryCompetency as PilotCompetency)
      ? (threat.primaryCompetency as PilotCompetency)
      : 'SAW'; // Default to SAW if invalid

    // Validate and map secondary competencies
    const secondaryComps = (threat.secondaryCompetencies || [])
      .filter(c => PILOT_COMPETENCIES.includes(c as PilotCompetency))
      .map(c => c as PilotCompetency);

    return {
      category: threat.category || 'Other',
      description: threat.description || '',
      primaryCompetency: primaryComp,
      secondaryCompetencies: secondaryComps,
      management: (threat.management as ThreatManagement) || undefined,
      evidence: threat.evidence,
    };
  });

  // Map errors - keep structure if available
  const errors: (TEMError | string)[] = (partBData.errors || []).map(error => {
    if (typeof error === 'string') return error;
    return {
      type: (error.type as ErrorType) || 'Other',
      classification: (error.classification as ErrorClassification) || undefined,
      description: error.description || '',
      ledToUAS: error.ledToUAS,
      evidence: error.evidence,
    };
  });

  // Map resilience factors - keep structure if available
  const resilienceFactors: (TEMResilienceFactor | string)[] = (partBData.resilienceFactors || []).map(rf => {
    if (typeof rf === 'string') return rf;
    return {
      description: rf.description || '',
      competencies: (rf.competencies || [])
        .filter(c => PILOT_COMPETENCIES.includes(c as PilotCompetency))
        .map(c => c as PilotCompetency),
      evidence: rf.evidence,
    };
  });

  // Map UAS events
  const uasEvents: TEMUAS[] = (partBData.uasEvents || []).map(u => ({
    description: u.description || '',
    severity: (u.severity as TEMUAS['severity']) || undefined,
    recovery: u.recovery,
    evidence: u.evidence,
  }));

  return {
    temOverview: partBData.operationalContext || partBData.managementSummary || 'TEM analysis based on session transcript.',
    threats,
    errors,
    resilienceFactors,
    uasEvents,
  };
}
